import React from 'react';
import type { PillarDef, PillarResult } from '../lib/pillarScoring';
import { IconMoon, IconActivity, IconSalad, IconBrain, IconUsers, IconShield } from './Icons';
import InfoTooltip from './InfoTooltip';
import { iconForPillarLabel } from '../lib/statusIcons';
import { getMissingSignals } from '../lib/expectedSignals';

const ICON_MAP: Record<string, React.FC<{ size?: number; color?: string }>> = {
  moon: IconMoon,
  activity: IconActivity,
  salad: IconSalad,
  brain: IconBrain,
  users: IconUsers,
  shield: IconShield,
};

/** Build a concise tooltip summarizing time window, data sources, and normal variation. */
function buildTooltipText(def: PillarDef, rangeDays?: number): string {
  const window = rangeDays && rangeDays > 1 ? `${rangeDays}-day rolling average.` : 'Prior day snapshot.';
  return `${window} ${def.source} Day-to-day fluctuation is normal — focus on your trend over weeks, not individual days.`;
}

interface Props {
  def: PillarDef;
  result: PillarResult;
  delta?: number;
  onClick: () => void;
  sparkline?: number[];
  rangeDays?: number;
}

function TrendArrow({ delta }: { delta: number }) {
  if (Math.abs(delta) <= 2) {
    return (
      <span className="inline-flex items-center text-[10px] font-semibold text-gray-400 ml-1">
        —
      </span>
    );
  }
  const improving = delta > 0;
  const color = improving ? '#16A34A' : '#EA580C';
  const arrow = improving ? '▲' : '▼';
  return (
    <span
      className="inline-flex items-center text-[10px] font-semibold ml-1"
      style={{ color }}
    >
      {arrow}&thinsp;{Math.abs(Math.round(delta))}
    </span>
  );
}

/** Tiny inline sparkline SVG */
function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  const valid = data.filter(v => isFinite(v) && !isNaN(v));
  if (valid.length < 2) return null;
  const w = 56, h = 18, pad = 1;
  const min = Math.min(...valid) - 5;
  const max = Math.max(...valid) + 5;
  const range = max - min || 1;
  const pts = valid.map((v, i) => {
    const x = pad + (i / (valid.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  });
  const polyline = pts.join(' ');
  // Area fill: close path along bottom
  const areaPath = `${pts.join(' ')} ${w - pad},${h - pad} ${pad},${h - pad}`;

  return (
    <svg width={w} height={h} className="flex-shrink-0" style={{ opacity: 0.85 }}>
      <polygon points={areaPath} fill={color} opacity={0.12} />
      <polyline points={polyline} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      {/* Latest point dot */}
      {pts.length > 0 && (() => {
        const [cx, cy] = pts[pts.length - 1].split(',').map(Number);
        return <circle cx={cx} cy={cy} r={2} fill={color} />;
      })()}
    </svg>
  );
}

export default function PillarCard({ def, result, delta, onClick, sparkline, rangeDays }: Props) {
  const Icon = ICON_MAP[def.icon] ?? IconBrain;
  const hasScore = result.score != null;

  return (
    <button
      onClick={onClick}
      className="flex flex-col bg-gray-50 dark:bg-dark-card rounded-xl p-4 lg:p-5 text-left hover:bg-gray-100 dark:hover:bg-dark-card/80 hover:shadow-card-hover transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-arete-blue/30 w-full"
    >
      {/* Top row: Icon + Name on left, trend arrow + tooltip on right */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${result.color}12` }}
          >
            <Icon size={14} color={result.color} />
          </div>
          <span className="font-medium uppercase text-[0.68rem] leading-tight text-arete-forest dark:text-gray-200" style={{ letterSpacing: '0.08em' }}>
            {def.name}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {hasScore && delta != null && <TrendArrow delta={delta} />}
          <span onClick={e => e.stopPropagation()}>
            <InfoTooltip text={buildTooltipText(def, rangeDays)} ariaLabel={`${def.name} score info`} />
          </span>
        </div>
      </div>

      {/* Hero row: Status label (primary) + score (secondary) */}
      <div className="flex items-baseline justify-between mb-1">
        {(() => {
          const icon = iconForPillarLabel(result.label);
          return (
            <span className="text-sm font-bold" style={{ color: result.color }}>
              {icon}{icon ? ' ' : ''}{result.label}
            </span>
          );
        })()}
        {hasScore && (
          <span className="text-xs font-medium text-arete-muted dark:text-gray-400">
            {result.score}<span className="text-[10px] font-normal">/100</span>
          </span>
        )}
      </div>

      {/* Sparkline + sub-metric row */}
      <div className="flex items-center gap-2">
        {result.subMetric && (
          <span className="text-[10px] text-arete-muted dark:text-gray-400 truncate">{result.subMetric}</span>
        )}
        {sparkline && sparkline.length >= 2 && (
          <div className="ml-auto">
            <MiniSparkline data={sparkline} color={result.color} />
          </div>
        )}
      </div>

      {/* Key secondary metrics for Sleep pillar (circadian + consistency) */}
      {def.key === 'sleep' && result.inputs.length > 0 && (() => {
        const circadian = result.inputs.find(i => i.label === 'Circadian Phase');
        const consistency = result.inputs.find(i => i.label === 'Sleep Consistency');
        if (!circadian && !consistency) return null;
        return (
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {circadian && (
              <span className="text-[9px] text-arete-muted dark:text-gray-500">
                Phase: {circadian.rawValue.split(' ')[0]}
              </span>
            )}
            {consistency && (
              <span className="text-[9px] text-arete-muted dark:text-gray-500">
                CV: {consistency.rawValue.split(' ')[0]}
              </span>
            )}
          </div>
        );
      })()}

      {/* Home-leaving indicator for Social pillar */}
      {def.key === 'social' && result.inputs.length > 0 && (() => {
        const hl = result.inputs.find(i => i.label === 'Home Leaving');
        if (!hl) return null;
        return (
          <span className="text-[9px] text-arete-muted dark:text-gray-500 mt-0.5">
            {hl.rawValue}
          </span>
        );
      })()}

      {/* Missingness badge — shows data completeness and which signals are missing */}
      {hasScore && (() => {
        const missing = getMissingSignals(def.key, result.inputs.map(i => i.label));
        if (missing.length === 0) return null;
        return (
          <span className="text-[9px] text-arete-muted dark:text-gray-500 mt-0.5" title={missing.map(s => `${s.label}: ${s.howToAdd}`).join('\n')}>
            {result.inputCount}/{result.inputCount + missing.length} signals · missing: {missing.map(s => s.label).join(', ')}
          </span>
        );
      })()}

      <p className="text-[10px] text-arete-muted dark:text-gray-400 mt-1.5 leading-tight">
        {def.description}
        {hasScore && result.inputs.length > 0 && (() => {
          const top = result.inputs.reduce((best, i) => i.weight > best.weight ? i : best, result.inputs[0]);
          return top ? ` · Top signal: ${top.label} ${top.rawValue}` : '';
        })()}
      </p>
    </button>
  );
}
